import { CheckoutEventsPaymentMethodCardTypes } from '@paddle/paddle-js';
import {
  BillingApiData,
  DiscountApiData,
  LineItem,
  PlanApiData,
  PlanId, SubscriptionStatus
} from 'src/app/pages/new-billing/types/api.types';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { monthlyMultiplierByRecurrence, planIdSwitch, pricePeriodByBillingPeriod } from 'src/app/pages/new-billing/utils/plan-maps';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';


export type Discount = {
  id: string;

  /** Represents the discount code. This will always match the {@link DiscountApiData.discountId} */
  code: string;

  /** Represents the discount value in the currency of the user's account */
  value: number;

  /** Represents the discount value as a percentage of the total price */
  percentage: number;

  /** The number of billing periods (in months) the discount is applied to */
  recurringIntervals: number;

  /** The type of discount */
  type?: 'percentage' | 'amount' | 'amount_per_seat';
};

export type AddOn = PlanApiData & LineItem;

export interface PaymentModel {
  cardType: CheckoutEventsPaymentMethodCardTypes | null;
  cardLast4: string | null;
  nextPaymentDate: string;
  nextPaymentAmount: number;
  nextPaymentAmountCalculated: number;
  nextPaymentPerUser: number;
  annualPayDiscount: number;
  paymentRecurrence: PricePeriodicity;
  priceMonthly: number;
  pricePerUser: number;
  totalBillingPrice: number;
  lastPaymentError?: string;
  lastPaymentAt?: string;
  tax: number;
  minBillableQuantity: number;
  billableUsers: number;
  discount: Discount | null;
  hasAddons: boolean;
  addOns?: AddOn[];
  addOnsAmount?: number;

  switchRecurrence(recurrence: PricePeriodicity): void;
}

const PAYMENT_DECIMALS = 100;

const oldPricingPlans = new Set(['standard_monthly', 'standard_yearly', 'basic_monthly', 'basic_yearly']);

export class PaymentModelImp implements PaymentModel {
  private _discount: DiscountApiData | null;
  private readonly _availableDiscounts: DiscountApiData[];
  private readonly _status: SubscriptionStatus;
  private readonly _initialPlanData: {
    plan: string;
    plans: PlanApiData[];
    discount: DiscountApiData | null;
    discountCode: string;
    paymentRecurrence: PricePeriodicity;
  };
  private _planId: PlanId;
  private _paymentRecurrence: PricePeriodicity;

  /** Holds a discount code that is set externally, e.g., from a query parameter.
   * It may or may not match with {@link DiscountApiData.discountId} */
  private _discountCode: string;
  private _freeUsers: number;
  private readonly _billingDetails: any;

  cardLast4: string;
  readonly lastPaymentError: string;
  readonly lastPaymentAt: string;
  readonly priceMonthly: number;
  readonly nextPaymentDate: string;
  readonly nextPaymentAmount: number;
  readonly unitPrice: number;
  readonly tax: number;
  readonly minBillableQuantity: number;
  readonly addOns: AddOn[];
  enableDiscountRemoval: boolean;

  cardType: CheckoutEventsPaymentMethodCardTypes | null;
  cardExpiryDate: string;

  get paymentRecurrence(): PricePeriodicity { return this._paymentRecurrence; }

  set paymentRecurrence(value: PricePeriodicity) {
    this._paymentRecurrence = value;
  }

  get monthlyMultipier(): number {
    return monthlyMultiplierByRecurrence[this.paymentRecurrence];
  }

  get discountedMonthlyMultiplier() {
    const discountedMonths = this.paymentRecurrence === 'year' ? 2 : 0;
    return this.monthlyMultipier - discountedMonths;
  }

  get pricePerUser() {
    return this.priceMonthly * this.discountedMonthlyMultiplier;
  }

  get totalBillingPrice() {
    return this.priceMonthly * this.billableUsers * this.monthlyMultipier;
  }

  get annualPayDiscount() {
    return this.paymentRecurrence === 'year'
      ? this.priceMonthly * this.billableUsers * 2
      : 0;
  }

  get addOnsAmount() {
    return this.addOns.reduce((acc, addOn) => {
      const localizedUnitPrice = this.getLocalizedAddonPrice(addOn);
      return acc + (localizedUnitPrice * addOn.quantity);
    }, 0);
  }

  get nextPaymentAmountCalculated() {
    const discount = this.discount?.value || 0;
    const subtotal = this.totalBillingPrice - this.annualPayDiscount - discount + this.addOnsAmount;

    return subtotal < 0 ? 0 : subtotal;
  }

  get nextPaymentPerUser() {
    return this.nextPaymentAmountCalculated / this.billableUsers;
  }

  get billableUsers() {
    const totalUsers = this.usersCount;
    const billableUsersAfterFree = Math.max(0, totalUsers - this._freeUsers);

    if (this.minBillableQuantity > billableUsersAfterFree) return this.minBillableQuantity;

    return billableUsersAfterFree;
  }

  get discount(): Discount | null {
    if (this.cardLast4) {
      const discountToParse = this._status === 'trial'
        ? this.getDiscountToParse(this.findAppliedDiscount())
        : this._discount;
      return this.parseDiscount(discountToParse);
    }

    const foundDiscount = this.findDiscountByCode();
    return this.parseDiscount(this._discount || foundDiscount);
  }

  get hasAddons() {
    return this.addOns.length > 0;
  }

  constructor({ billingDetails, plans, discounts, discountCode }: BillingApiData,
    private usersCount: number, enableDiscountRemoval: boolean,
    private readonly localizedPricingService: LocalizedPricingService) {
    const currentPlan = plans.find(plan => plan.planId === billingDetails.currentPlanId);

    this._planId = currentPlan.planId;
    this.cardLast4 = billingDetails.cardLastFourDigits || null;
    this.cardType = billingDetails.cardType ? billingDetails.cardType as CheckoutEventsPaymentMethodCardTypes : null;
    this.unitPrice = currentPlan.unitPrice;
    this.paymentRecurrence = pricePeriodByBillingPeriod[currentPlan.billingPeriod];
    this.priceMonthly = this.getLocalizedPlanUnitPrice(plans, currentPlan, billingDetails);
    this.addOns = billingDetails?.lineItems?.map(addOn => ({
      ...plans.find(plan =>
        plan.planId === addOn.planId,
      ),
      ...addOn,
    }));
    this.lastPaymentError = billingDetails.lastPaymentError || null;
    this.lastPaymentAt = billingDetails.lastPaymentAt || null;
    this.nextPaymentDate = billingDetails.nextPaymentDate;
    this.nextPaymentAmount = billingDetails.nextPaymentAmount / PAYMENT_DECIMALS;
    this.tax = billingDetails.tax / PAYMENT_DECIMALS || 0;
    this.minBillableQuantity = billingDetails.minBillableQuantity || 0;
    this.enableDiscountRemoval = enableDiscountRemoval;
    this._initialPlanData = {
      plan: billingDetails.currentPlan,
      plans,
      discount: billingDetails.discount,
      discountCode: discountCode.toUpperCase(),
      paymentRecurrence: pricePeriodByBillingPeriod[currentPlan.billingPeriod],
    };
    // If the current plan is the same as the one in the billing details, apply the discount
    if (enableDiscountRemoval) {
      if (billingDetails.currentPlan === currentPlan.name) {
        this._discountCode = discountCode.toUpperCase();
        this._discount = billingDetails.discount;
      } else {
        this._discount = null;
        this._discountCode = null;
      }
    } else {
      this._discountCode = discountCode.toUpperCase();
      this._discount = billingDetails.discount;
    }
    this._availableDiscounts = discounts;
    this._status = billingDetails.customerStatus;
    this._freeUsers = billingDetails?.freeUsers || 0;
    this._billingDetails = billingDetails;
  }

  //TODO: Update this function later once active/current property is available already from the back-end service
  getActivePlan(plans: PlanApiData[] | undefined, currentPlan: PlanApiData): PlanApiData | undefined {
    if (!plans || plans.length === 0) return undefined;

    if (oldPricingPlans.has(currentPlan.planId)) {
      return plans.find(plan => plan.productId === currentPlan.productId && plan.billingPeriod === 'monthly' && plan.type === 'plan');
    }

    // Iterate from the end to find the most recent plan matching the criteria
    for (let i = plans.length - 1; i >= 0; i--) {
      const plan = plans[i];
      if (plan.productId === currentPlan.productId && plan.billingPeriod === 'monthly' && plan.type === 'plan') {
        return plan;
      }
    }

    return undefined;
  }

  /**
   * Gets the localized unit price for a plan based on billing currency or geo-location
   * @param plans - Array of available plans
   * @param currentPlan - The current plan
   * @param billingDetails - Billing details containing currency information
   * @returns The localized unit price in dollars
   */
  private getLocalizedPlanUnitPrice(plans: PlanApiData[], currentPlan: PlanApiData, billingDetails: any): number {
    const activePlan = this.getActivePlan(plans, currentPlan);
    if (!activePlan) {
      return 0;
    }

    // Priority 1: Use billing currency if available
    if (billingDetails.currency && activePlan.priceOverrides) {
      const override = activePlan.priceOverrides.find(
        po => po.currencyCode === billingDetails.currency,
      );
      if (override) {
        return +override.unitPrice / 100;
      }
    }

    // Priority 2: Use geo-based pricing if localized pricing is enabled
    if (this.localizedPricingService?.isLocalizedPricingEnabled() && activePlan.priceOverrides && !this._billingDetails?.cardLast4) {
      // Use cached geo info for synchronous operation
      const cachedGeoInfo = this.localizedPricingService?.getCachedGeoInfo();
      const userCountryCode = cachedGeoInfo?.geo?.country;

      if (userCountryCode) {
        const matchingOverride = activePlan.priceOverrides.find(
          override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
        );
        if (matchingOverride) {
          return +matchingOverride.unitPrice / 100;
        }
      }
    }

    // Priority 3: Default to original unit price
    return this.getActivePlan(plans, currentPlan)?.unitPrice || 0;
  }

  /**
   * Gets the localized unit price for an addon based on billing currency or geo-location
   * Follows the same priority system as main plan pricing:
   * 1. Billing currency with price overrides
   * 2. Geo-based pricing (if localized pricing is enabled)
   * 3. Default USD pricing
   * @param addOn - The addon to get localized price for
   * @returns The localized unit price in dollars
   */
  private getLocalizedAddonPrice(addOn: AddOn): number {
    // Priority 1: Use billing currency if available
    const billingCurrency = this._billingDetails?.currency;
    if (billingCurrency && addOn.priceOverrides) {
      const override = addOn.priceOverrides.find(
        po => po.currencyCode === billingCurrency,
      );
      if (override) {
        return +override.unitPrice / 100;
      }
    }

    // Priority 2: Use geo-based pricing if localized pricing is enabled
    if (this.localizedPricingService.isLocalizedPricingEnabled() && addOn.priceOverrides && !this._billingDetails?.cardLast4) {
      // Use cached geo info for synchronous operation
      const cachedGeoInfo = this.localizedPricingService.getCachedGeoInfo();
      const userCountryCode = cachedGeoInfo?.geo?.country;

      if (userCountryCode) {
        const matchingOverride = addOn.priceOverrides.find(
          override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
        );
        if (matchingOverride) {
          return +matchingOverride.unitPrice / 100;
        }
      }
    }

    // Priority 3: Default to original unit price
    return addOn.unitPrice || 0;
  }

  switchRecurrence(recurrence: PricePeriodicity) {
    const currentPlan = this._initialPlanData.plans.find(plan => plan.planId === planIdSwitch[this._planId]);

    // On switching recurrence, the discount is removed if the current plan is different from the initial plan
    if (this.enableDiscountRemoval) {
      if (this._initialPlanData.paymentRecurrence !== recurrence) {
        this._discount = null;
        this._discountCode = null;
      } else if (currentPlan.name === this._initialPlanData.plan) {
        this._discountCode = this._initialPlanData.discountCode;
        this._discount = this._initialPlanData.discount;
      }
    }
    this._planId = planIdSwitch[this._planId];
    this.paymentRecurrence = recurrence;
  }

  parseDiscount(rawDiscount: DiscountApiData): Discount | null {
    if (!rawDiscount) return null;

    const value = rawDiscount.type === 'percentage' ? this.pricePerUser * (rawDiscount.value / 100) : rawDiscount.value;
    const discountPeriod = rawDiscount.period === 'All billing periods' ? 0 : rawDiscount.period;
    const recurringIntervals = discountPeriod * this.monthlyMultipier;

    return {
      id: rawDiscount.externalDiscountId,
      code: rawDiscount.discountId,
      value,
      percentage: rawDiscount.value,
      recurringIntervals,
      type: rawDiscount.type,
    };
  }

  private findAppliedDiscount(): DiscountApiData | undefined {
    return this._availableDiscounts.find(discount => discount.discountId === this._discount?.discountId);
  }

  private findDiscountByCode(): DiscountApiData | undefined {
    return this._availableDiscounts.find(discount => {
      const discountCode = discount.discountId.toUpperCase();
      const isPlanAvailable = !discount.availablePlans || discount.availablePlans.includes(this._planId);

      return isPlanAvailable && discountCode === this._discountCode;
    });
  }

  private getDiscountToParse(discount: DiscountApiData | undefined): DiscountApiData | null {
    return (!discount?.availablePlans || discount.availablePlans.length === 0 || discount.availablePlans.includes(this._planId))
      ? this._discount
      : null;
  }

  static capitalizeWords(text: string): string {
    try {
      if (!text) return text || '';

      let result = text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

      if (result.toLowerCase().includes('big query')) {
        result = result.replace(/big query/gi, 'BigQuery');
      }

      if (result.toLowerCase().includes('ai')) {
        result = result.replace(/Ai/gi, 'AI');
      }

      return result;
    } catch (error) {
      return text;
    }
  }

  static getCompleteTitle(keyPhrase: string): string {
    try {
      if (!keyPhrase) return keyPhrase;

      if (keyPhrase.includes('(') && keyPhrase.includes(')')) {
        return keyPhrase;
      }

      const acronym = PaymentModelImp.getAcronymFromTitle(keyPhrase);
      if (!acronym || acronym === keyPhrase) {
        return keyPhrase;
      }

      const completeTitleFromAcronym = PaymentModelImp.getTitleFromAcronym(acronym);

      if (completeTitleFromAcronym && completeTitleFromAcronym !== acronym) {
        return completeTitleFromAcronym;
      }

      const normalizedTitle = PaymentModelImp.capitalizeWords(keyPhrase);
      return `${normalizedTitle} (${acronym})`;
    } catch (error) {
      return keyPhrase;
    }
  }

  private static readonly addonAcronymMap: Record<string, string> = {
    'office vs remote report': 'OVR',
    'unusual activity report': 'UAR',
    'single sign on': 'SSO',
    'automated user provisioning': 'AUP',
    'big query access': 'BQA',
    'bigquery access': 'BQA',
    'video screen recording': 'VSR',
    'benchmarks ai': 'BAI',
    benchmarks: 'BAI',
    'meeting insights': 'MI',
  };


  private static readonly addOnTitleAcronymMap: Record<string, string> = {
    OVR: 'Office vs Remote Report',
    UAR: 'Unusual Activity Report',
    SSO: 'Single Sign On',
    AUP: 'Automated User Provisioning',
    BQA: 'BigQuery Access',
    VSR: 'Video Screen Recording',
    BAI: 'Benchmarks AI',
    BENCHMARKS: 'Benchmarks AI',
    MI: 'Meeting Insights',
  };

  static getAcronymFromTitle(title: string): string {
    if (!title) return title;

    const normalizedTitle = title.toLowerCase().trim();

    for (const [keyPhrase, acronym] of Object.entries(PaymentModelImp.addonAcronymMap)) {
      if (normalizedTitle.includes(keyPhrase)) {
        return acronym;
      }
    }

    return title;
  }

  static getTitleFromAcronym(acronym: string): string {
    if (!acronym) return acronym;

    const normalizedAcronym = acronym.toUpperCase().trim().replace(/\s*(?:Add-on|-)\s*/gi, ' ').trim();

    for (const [keyAcronym, title] of Object.entries(PaymentModelImp.addOnTitleAcronymMap)) {
      if (normalizedAcronym === keyAcronym) {
        return `${title} (${keyAcronym})`;
      }
    }

    return acronym;
  }

  static getAcronymWithPrice(title: string): string {
    if (!title || typeof title !== 'string') return title;

    const acronym = PaymentModelImp.getAcronymFromTitle(title).trim();
    if (acronym === title) return title;

    const dollarIndex = title.indexOf('$');
    if (dollarIndex !== -1) {
      const price = title.substring(dollarIndex).trim();
      return `${acronym} ${price}`;
    }

    return acronym;
  }

  static getCompleteTitleWithPrice(title: string): string {
    if (!title || typeof title !== 'string') return title;

    const dollarIndex = title.indexOf('$');
    let titleWithoutPrice = title;
    let price = '';

    if (dollarIndex !== -1) {
      titleWithoutPrice = title.substring(0, dollarIndex).trim();
      price = title.substring(dollarIndex).trim();
    }

    titleWithoutPrice = titleWithoutPrice.replace(/\s*(?:Add-on|-)\s*/gi, ' ').trim();

    let completeTitle = PaymentModelImp.getCompleteTitle(titleWithoutPrice);
    if (completeTitle === titleWithoutPrice) {
      completeTitle = PaymentModelImp.getTitleFromAcronym(titleWithoutPrice);
    }

    if (price) {
      return `${completeTitle} ${price}`;
    }

    return completeTitle;
  }
}
